/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable prettier/prettier */
import React, { FC, useCallback, useEffect, useState, ChangeEventHandler } from 'react';

import router from "next/router";
import { Formik, FormikHelpers, FormikProps } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import Checkbox from "react-custom-checkbox";
import RadioButton, { RadioOptions } from "../../components/FormFields/RadioButton";
import SelectField, { SelectFieldOption } from "../../components/FormFields/SelectField";
import User, { TMentor, TRole } from "../../models/auth/@types";

import { AUTH_ERRORS } from '../../Constants/ErrorMessages';
import Button from '../../components/Button';
import { ROUTES } from '../../Constants/variables';
import StateCityModel from '../../models/StateCity';
import TextField from '../../components/FormFields/TextField';
import Typo from '../../components/Typo';
import { getFieldError } from '../../components/FormFields/Utils';
import { logger } from '../../utils';
import styles from './userEditForm.module.sass';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import StudentInfoFields from './StudentInfoFields';
import { KEYS, STUDENT_INFO_KEYS } from './constants';

export const ROLE_OPTIONS: RadioOptions[] = [
  {
    name: 'Student',
    value: TRole.STUDENT,
    id: TRole.STUDENT,
  },
  { name: 'Teacher', value: TRole.TEACHER, id: TRole.TEACHER },
  { name: 'Mentor', value: TRole.MENTOR, id: TRole.MENTOR },
  { name: 'Admin', value: TRole.ADMIN, id: TRole.ADMIN },
];

export const ADMIN_TYPES: RadioOptions[] = [
  {
    name: "Super",
    value: 1,
    id: "super",
  },
  { name: "Only the following", value: 0, id: "piecemeal" },
]

const ValidationSchema = Yup.object().shape({
  [KEYS.firstNameKey]: Yup.string().required(AUTH_ERRORS.FIRST_NAME_REQUIRED),
  [KEYS.lastNameKey]: Yup.string().required(AUTH_ERRORS.LAST_NAME_REQUIRED),
  [KEYS.schoolNameKey]: Yup.string().required(AUTH_ERRORS.SCHOOL_REQUIRED),
  [KEYS.emailKey]: Yup.string().required(AUTH_ERRORS.EMAIL_REQUIRED),
  [KEYS.industryNameKey]: Yup.string()
    .nullable()
    .when('role', {
      is: TRole.MENTOR,
      then: schema => schema.required(AUTH_ERRORS.INDUSTRY_REQUIRED),
    }),
  [KEYS.cityNameKey]: Yup.string().nullable(),
  [KEYS.descriptionKey]: Yup.string().nullable(),
  [KEYS.stateNameKey]: Yup.string()
    .nullable()
    .when('role', {
      is: (value: TRole) => [TRole.STUDENT, TRole.TEACHER].includes(value),
      then: schema => schema.required(AUTH_ERRORS.STATE_REQUIRED),
    }),
  student_info: Yup.object().shape({
    guardian_name: Yup.string(),
    guardian_email: Yup.string().email(AUTH_ERRORS.EMAIL_VALIDATION_NOT_MATCH),
    teacher_name: Yup.string(),
    teacher_email: Yup.string().email(AUTH_ERRORS.EMAIL_VALIDATION_NOT_MATCH),
  }),
});

interface IUser {
  user: User;
  onUpdate: (
    values: Partial<User>,
    formikHelpers: FormikHelpers<Partial<User>>,
  ) => Promise<unknown>;
  industryOptions: SelectFieldOption[];
}

const UserEditForm: FC<IUser> = props => {
  const { user, onUpdate, industryOptions } = props;
  const { isMobile } = useIsMobileViewport();
  const userPermissions = user.user_permissions;
  const [selectedRole, setSelectedRole] = useState<TRole>(user.role);
  const [selectedSuper, setSelectedSuper] = useState<number>(userPermissions?.super ?? 0);
  const [stateOptions, setStateOptions] = useState<SelectFieldOption[]>([]);
  const [cityOptions, setCityOptions] = useState<SelectFieldOption[]>([]);
  const [selectedState, setSelectedState] = useState<string>();
  const [fetchingState, setFetchingState] = useState<boolean>();
  const [fetchingCities, setFetchingCities] = useState<boolean>();
  useEffect(() => {
    async function fetchStateData() {
      try {
        setFetchingState(true);
        const response = (await StateCityModel.getUSStates()).data.map(
          s =>
            ({
              id: s.id,
              name: s.name,
              value: s.name,
            }) as SelectFieldOption,
        );
        setStateOptions(response);
        setFetchingState(false);
      } catch (error) {
        setFetchingState(true);
        logger.error(error);
      }
    }
    fetchStateData();
  }, []);

  useEffect(() => {
    async function fetchStateCityData() {
      if (!selectedState) return;
      try {
        setFetchingCities(true);
        const {
          data: { cities, stateName },
        } = await StateCityModel.getUSStateCities(selectedState as string);
        const response = cities.map(
          s =>
            ({
              id: s.id,
              name: s.name,
              value: s.name,
            }) as SelectFieldOption,
        );
        setFetchingCities(false);
        stateName.toLowerCase() === selectedState.toLowerCase()
          ? setCityOptions(response)
          : setCityOptions([]);
      } catch (error) {
        setFetchingCities(true);
        logger.error(error);
      }
    }
    fetchStateCityData();
  }, [selectedState]);

  useEffect(() => {
    setSelectedState(user?.state);
  }, [user]);

  const handleCancelChange = async () => {
    return router.push(ROUTES.MANAGE_USER_DASHBOARD);
  };

  const checkMatchingCityForState = useCallback(
    (cityName: string) => {
      if (!cityName || cityOptions.length === 0) return true;
      return cityOptions.some(e => {
        if (typeof e.value === 'string') {
          return e.value.toLowerCase() === cityName.toLowerCase();
        }
        return null;
      });
    },
    [cityOptions],
  );

  const isCityRequired = useCallback(
    (city: any, state: any) => {
      return (state || '').length !== 0 && (city || '').length === 0 && cityOptions.length !== 0;
    },
    [cityOptions.length],
  );

  return (
    <div className={styles.userEditFormContainer}>
      <Typo normal> Note: All fields are required </Typo>
      <Formik<Partial<User>>
        initialValues={
          user?.role === TRole.MENTOR && user?.state === null
            ? { ...user, city: null as unknown as undefined }
            : { ...user, ...user.user_permissions, id: user.id, }
        }
        onSubmit={(values, formikHelpers) => {
          onUpdate(values, formikHelpers);
        }}
        validationSchema={ValidationSchema}
        enableReinitialize
      >
        {(formikProps: FormikProps<Partial<User>>) => {
          const isCityInState = checkMatchingCityForState(formikProps?.values?.city as string);
          if (!isCityInState) formikProps.values.city = undefined;
          const errors = {
            firstName: getFieldError(KEYS.firstNameKey, formikProps),
            lastName: getFieldError(KEYS.lastNameKey, formikProps),
            school: getFieldError(KEYS.schoolNameKey, formikProps),
            industry: getFieldError(KEYS.industryNameKey, formikProps),
            state: getFieldError(KEYS.stateNameKey, formikProps),
            city: getFieldError(KEYS.cityNameKey, formikProps),
            description: getFieldError(KEYS.descriptionKey, formikProps),
            email: getFieldError(KEYS.emailKey, formikProps),
            role: getFieldError(KEYS.roleKey, formikProps),
            guardianName: getFieldError(STUDENT_INFO_KEYS.guardianNameKey, formikProps),
            guardianEmail: getFieldError(STUDENT_INFO_KEYS.guardianEmailKey, formikProps),
            teacherName: getFieldError(STUDENT_INFO_KEYS.teacherNameKey, formikProps),
            teacherEmail: getFieldError(STUDENT_INFO_KEYS.teacherEmailKey, formikProps),
          };

          if (isCityRequired(formikProps.values.city, formikProps.values.state)) {
            errors.city = AUTH_ERRORS.CITY_REQUIRED;
          }
          return (
            <form onSubmit={formikProps.handleSubmit} id="user_edit_form">
              <div className="p-4">
                <label htmlFor="Type">
                  <Typo classes="mt-5 mb-2 ml-2">Type</Typo>
                </label>
                <div className={styles.radioButtonContainer}>
                  <RadioButton
                    valueKey={KEYS.roleKey}
                    options={ROLE_OPTIONS}
                    rootProps={{ className: styles.radioButton }}
                    onChange={(e, val) => {
                      formikProps.handleChange(e);
                      formikProps.setFieldValue('role', val);
                      setSelectedRole(val);
                    }}
                    otheFieldProps={{ onBlur: formikProps.handleBlur }}
                    selectedValue={selectedRole ?? ''}
                    error={errors.role}
                  />
                </div>
                {selectedRole === TRole.ADMIN && (
                  <>
                    <div>
                      <label htmlFor="Admin Tools Access">
                        <Typo classes="mt-5 mb-2 ml-2">Admin Tools Access</Typo>
                      </label>
                      <RadioButton
                        valueKey={KEYS.super}
                        options={ADMIN_TYPES}
                        rootProps={{ className: styles.radioButton }}
                        onChange={(e, val) => {
                          formikProps.handleChange(e);
                          const newValue = parseInt(val, 10)
                          formikProps.setFieldValue("super", newValue);
                          setSelectedSuper(newValue);
                        }}
                        otheFieldProps={{ onBlur: formikProps.handleBlur }}
                        selectedValue={selectedSuper ?? 0}
                        error={errors.role} />
                    </div>
                        <Checkbox
                          valueKey={"quick_reports"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>View Quick Reports</Typo>}
                          id="quick_reports"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.quick_reports ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("quick_reports", value);
                          }} />
                        <Checkbox
                          valueKey={"manage_skillbuilders"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>Manage Skillbuilders</Typo>}
                          id="manage_skillbuilders"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.manage_skillbuilders ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("manage_skillbuilders", value);
                          }} />
                        <Checkbox
                          valueKey={"manage_users"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>Manage Users</Typo>}
                          id="manage_users"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.manage_users ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("manage_users", value);
                          }} />
                        <Checkbox
                          valueKey={"manage_projects"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>Manage Projects</Typo>}
                          id="manage_projects"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.manage_projects ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("manage_projects", value);
                          }} />
                        <Checkbox
                          valueKey={"manage_feedback"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>Manage Mentor Feedback Requests</Typo>}
                          id="manage_feedback"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.manage_feedback ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("manage_feedback", value);
                          }} />
                        <Checkbox
                          valueKey={"manage_surveys"}
                          disabled={selectedSuper}
                          label={<Typo classes={clsx(selectedSuper && styles.disabledText)}>Manage Projects Survey Results</Typo>}
                          id="manage_surveys"
                          className={clsx(styles.userCheckbox, selectedSuper && styles.disabled) }
                          checked={userPermissions?.manage_surveys ?? 0}
                          onChange={(value: boolean) => {
                            formikProps.setFieldValue("manage_surveys", value);
                          }} />
                  </>
                )}
                <label htmlFor="name">
                  <Typo classes="mt-5 mb-2  ml-2">Name</Typo>
                </label>
                <div className={styles.inputFieldsContainer}>
                  <TextField
                    valueKey={KEYS.firstNameKey}
                    type="text"
                    otherFieldProps={{
                      placeholder: 'First Name',
                      onChange: formikProps.handleChange,
                      value: formikProps.values.first_name ?? '',
                    }}
                    error={errors.firstName}
                  />
                  <TextField
                    valueKey={KEYS.lastNameKey}
                    type="text"
                    otherFieldProps={{
                      placeholder: 'Last Name',
                      onChange: formikProps.handleChange,
                      value: formikProps.values.last_name ?? '',
                    }}
                    error={errors.lastName}
                  />
                </div>
                <label htmlFor="email">
                  <Typo classes="mt-5 mb-2  ml-2">Email</Typo>
                </label>
                <div>
                  <TextField
                    valueKey={KEYS.emailKey}
                    type="email"
                    otherFieldProps={{
                      placeholder: 'Email',
                      onChange: formikProps.handleChange,
                      value: formikProps.values.email ?? '',
                    }}
                    error={errors.email}
                  />
                </div>
                <label htmlFor="What_your_school">
                  <Typo classes="mt-5 mb-2  ml-2">Organization</Typo>
                </label>
                <div>
                  <TextField
                    valueKey={KEYS.schoolNameKey}
                    type="text"
                    otherFieldProps={{
                      placeholder: 'Midtown High School',
                      onChange: formikProps.handleChange,
                      value: formikProps.values.organization ?? '',
                    }}
                    error={errors.school}
                  />
                  {selectedRole === TRole.MENTOR ? (
                    <div className="mt-5">
                      <SelectField
                        valueKey={KEYS.industryNameKey}
                        options={industryOptions}
                        otherProps={{
                          value: (formikProps.values as TMentor)?.industry ?? '',
                          onChange: formikProps.handleChange,
                          onBlur: formikProps.handleBlur
                        }}
                        error={errors.industry}
                      />
                    </div>
                  ) : (
                    <div className={`mt-5 ${styles.inputFieldsContainer}`}>
                      <SelectField
                        emptyOption="State"
                        valueKey={KEYS.stateNameKey}
                        options={stateOptions}
                        loading={fetchingState}
                        loadingMessage="Loading States"
                        otherProps={{
                          onChange: ((e: React.ChangeEvent<HTMLSelectElement>) => {
                            formikProps.handleChange(e);
                            setSelectedState(e.target.value);
                          }) as ChangeEventHandler<HTMLSelectElement>,
                          onBlur: formikProps.handleBlur,
                          value: (formikProps.values as User)?.state ?? selectedState,
                        }}
                        error={errors.state}
                      />
                      <SelectField
                        emptyOption="Nearest Town/City"
                        valueKey={KEYS.cityNameKey}
                        options={cityOptions}
                        loading={fetchingCities}
                        loadingMessage={`Loading ${selectedState}'s Cities`}
                        otherProps={{
                          value: (formikProps.values as User)?.city ?? '',
                          onChange: formikProps.handleChange,
                          onBlur: formikProps.handleBlur
                        }}
                        error={errors.city}
                        rootClassName={clsx(cityOptions.length === 0 && "is-invisible")}
                      />
                    </div>
                  )}
                </div>
                <label htmlFor="bio">
                  <Typo classes="mt-5 mb-2  ml-2"> Bio </Typo>
                </label>
                <div>
                  <TextField
                    valueKey={KEYS.descriptionKey}
                    type="text"
                    rows={9}
                    otherFieldProps={{
                      placeholder: 'Short Bio Instagram...',
                      onChange: formikProps.handleChange,
                      value: formikProps.values.bio ?? '',
                    }}
                    error={errors.description}
                  />
                </div>
                {selectedRole === TRole.STUDENT && (
                  <StudentInfoFields />
                )}
                <div
                  className={
                    isMobile
                      ? 'is-flex is-justify-content-center is-flex-direction-column'
                      : 'mt-5 mb-5'
                  }
                >
                  <Button
                    type="submit"
                    theme="Filled - Lime Green Gradient"
                    classes={isMobile ? 'mb-5 mt-6' : 'mr-5'}
                    disabled={
                      !formikProps.isValid ||
                      isCityRequired(formikProps.values.city, formikProps.values.state)
                    }
                  >
                    Save Changes
                  </Button>
                  <Button classes={styles.cancelChangesBtn} onClick={handleCancelChange}>
                    Cancel Changes
                  </Button>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default UserEditForm;
